import { formatPercentage } from '@/domain/common/format'
import { Percentage } from '@/domain/types/NumericValues'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/ui/atoms/dropdown/DropdownMenu'
import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { Typography } from '@/ui/atoms/typography/Typography'
import { SettingsButton } from '@/ui/molecules/settings-button/SettingsButton'
import { cn } from '@/ui/utils/style'
import { Slot } from '@radix-ui/react-slot'
import { ReactNode, forwardRef, useState } from 'react'

interface SlippagePanelProps {
  slippage: Percentage
  slippageOptions?: Percentage[]
  title?: string
  disabled?: boolean
  trigger?: ReactNode
  setSlippage: (slippage: Percentage) => void
}

const DEFAULT_SLIPPAGE_OPTIONS = [Percentage(0.001), Percentage(0.005), Percentage(0.01)]

type SlippageWarningLevel = 'none' | 'high' | 'very-high'

function getSlippageWarningLevel(slippage: Percentage): SlippageWarningLevel {
  const value = slippage.toNumber()
  if (value >= 0.2) return 'very-high'
  if (value >= 0.02) return 'high'
  return 'none'
}

export function SlippageWarning({ slippage }: { slippage: Percentage }) {
  const level = getSlippageWarningLevel(slippage)
  if (level === 'none') return null

  if (level === 'very-high') {
    return <span className="text-red-500">🚨 Very High</span>
  }

  return <span className="text-yellow-500">⚠️ High</span>
}

export function SlippagePanel({
  slippage,
  slippageOptions,
  title,
  setSlippage,
  disabled,
  trigger,
}: SlippagePanelProps) {
  const [customSlippage, setCustomSlippage] = useState('')
  const [isCustomInput, setIsCustomInput] = useState(false)

  function handleCustomSlippageChange(value: string) {
    setCustomSlippage(value)
    const numValue = Number.parseFloat(value)
    if (!Number.isNaN(numValue) && numValue > 0 && numValue < 50) {
      setSlippage(Percentage(numValue / 100))
    }
  }

  function handleOptionClick(option: Percentage) {
    setSlippage(option)
    setIsCustomInput(false)
    setCustomSlippage('')
  }

  function handleCustomInputClick() {
    setIsCustomInput(true)
    setCustomSlippage('')
  }

  const options = slippageOptions && slippageOptions.length > 0 ? slippageOptions : DEFAULT_SLIPPAGE_OPTIONS
  const isCustomSlippageActive = !options.some((option) => slippage.eq(option))

  const Wrapper = disabled ? DisabledTooltip : Slot

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Wrapper>{trigger || <SettingsButton />}</Wrapper>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[500] mt-2 border-border p-0">
        <div className="px-6 py-4 text-sm">
          <Typography variant="p" className="mb-3 font-bold text-sm">
            {title || 'Slippage'}
          </Typography>

          <div className="space-y-3">
            <div>
              <Typography variant="p" className="mb-2 text-secondary-foreground text-xs">
                Max slippage:
              </Typography>
              <div className="flex flex-wrap gap-2">
                {options.map((option) => (
                  <button
                    key={option.toString()}
                    onClick={() => handleOptionClick(option)}
                    className={cn(
                      'rounded border-white/20 px-2 py-1 text-secondary-foreground text-xs transition-colors hover:bg-accent hover:text-foreground',
                      slippage.eq(option) && 'border-primary bg-primary/10 text-primary',
                    )}
                  >
                    {formatPercentage(option, { minimumFractionDigits: 1 })}
                  </button>
                ))}
                <button
                  onClick={handleCustomInputClick}
                  className={cn(
                    'min-w-[60px] rounded border-white/20 px-2 py-1 text-secondary-foreground text-xs transition-colors hover:bg-accent hover:text-foreground',
                    isCustomSlippageActive && 'border-primary bg-primary/10 text-primary',
                  )}
                >
                  Custom
                </button>
              </div>
            </div>

            {isCustomInput && (
              <div className="space-y-2">
                <Typography variant="p" className="text-secondary-foreground text-xs">
                  Custom slippage (%):
                </Typography>
                <input
                  type="number"
                  value={customSlippage}
                  onChange={(e) => handleCustomSlippageChange(e.target.value)}
                  placeholder="0.5"
                  min="0.1"
                  max="49.9"
                  step="0.1"
                  className="w-full rounded border-white/20 bg-white/5 px-2 py-1 text-foreground text-xs [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:border-primary placeholder:text-muted-foreground focus:outline-none"
                />
                <Typography variant="prompt" className="text-xs">
                  Enter a value between 0.1% and 49.9%
                </Typography>
              </div>
            )}

            {!isCustomInput && (
              <div className="pt-2">
                <Typography variant="prompt" className="text-xs">
                  Current: {formatPercentage(slippage, { minimumFractionDigits: 1 })}{' '}
                  <SlippageWarning slippage={slippage} />
                </Typography>
              </div>
            )}

            {isCustomInput && getSlippageWarningLevel(slippage) !== 'none' && (
              <div className="pt-1">
                <Typography variant="prompt" className="text-xs">
                  <SlippageWarning slippage={slippage} />
                </Typography>
              </div>
            )}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface DisabledTooltipProps {
  children: ReactNode
}

const DisabledTooltip = forwardRef<HTMLSpanElement, DisabledTooltipProps>(function DisabledTooltip({ children }, ref) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span ref={ref}>{children}</span>
      </TooltipTrigger>
      <TooltipContentShort>Settings are disabled while actions are in progress.</TooltipContentShort>
    </Tooltip>
  )
})
